import React from 'react';
import PropTypes from 'prop-types';
import { Columns3 } from 'lucide-react';
import IconButton from '../ui/IconButton';
import Menu, { MenuItem } from '../ui/Menu';
import Tooltip from '../ui/Tooltip';
import { getColumnLabel } from './columnLayout';

// The leaf column defs, in order, with their TanStack ids (an `accessorKey`
// path's dots become underscores, as TanStack does).
function leafColumns(columns) {
    return columns.flatMap((column) => (
        column.columns
            ? leafColumns(column.columns)
            : [{ id: column.id ?? String(column.accessorKey).replaceAll('.', '_'), column }]
    ));
}

/**
 * A "Columns" icon button with a menu of checkbox items, one per column
 * that can hide (not `enableHiding: false`), for a panel header next to a
 * DataTable. `columns` are the table's column defs; `columnVisibility` and
 * `onColumnVisibilityChange(next)` are the same controlled state DataTable
 * takes. The last visible column cannot be hidden.
 */
export default function ColumnVisibilityMenu({
    columns,
    columnVisibility,
    onColumnVisibilityChange,
    label = 'Columns',
}) {
    const hideable = leafColumns(columns).filter(({ column }) => column.enableHiding !== false);
    const visibleCount = hideable.filter(({ id }) => columnVisibility[id] !== false).length;
    return (
        <Menu
            trigger={(
                <Tooltip content={label}>
                    <IconButton label={label} size="sm">
                        <Columns3 size={16} aria-hidden="true" />
                    </IconButton>
                </Tooltip>
            )}
        >
            {hideable.map(({ id, column }) => {
                const visible = columnVisibility[id] !== false;
                return (
                    <MenuItem
                        key={id}
                        type="checkbox"
                        checked={visible}
                        disabled={visible && visibleCount === 1}
                        onSelect={() => onColumnVisibilityChange({ ...columnVisibility, [id]: !visible })}
                    >
                        {getColumnLabel(column, id)}
                    </MenuItem>
                );
            })}
        </Menu>
    );
}

ColumnVisibilityMenu.propTypes = {
    columns: PropTypes.array.isRequired,
    columnVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
    onColumnVisibilityChange: PropTypes.func.isRequired,
    label: PropTypes.string,
};
